import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { Fragment } from "react";
import { css } from "styled-components";

interface Props {
    categories: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteCategoriesStyles;
    focusedQuery?: string;
}

export interface AutocompleteCategoriesStyles {
    headerText?: TypographyPresentationProps;
    categoryItemText?: TypographyPresentationProps;
    focusedCategoryItemText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
}

export const autocompleteCategoriesStyles: AutocompleteCategoriesStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            height: 24px;
            width: 260px;
            color: #333333;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 24px;
            text-transform: uppercase;
        `,
    },
    categoryItemText: {
        ellipsis: true,
        css: css`
            width: 100%;
            text-align: left;
            cursor: pointer;
            color: #333333;
            text-transform: uppercase;
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
    focusedCategoryItemText: {
        ellipsis: true,
        css: css`
            width: 100%;
            text-align: left;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                color: #333333 !important;
                text-transform: uppercase;
                font-size: 12px;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 26px;
                > div {
                    display: inline-block;
                }
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                line-height: 30px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            `,
        },
        css: css`
            width: auto;
            overflow: hidden;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: auto;
            overflow: hidden;
            background-color: lightgray;
        `,
    },
};

const styles = autocompleteCategoriesStyles;

class AutocompleteCategories extends React.Component<Props> {
    private readonly styles: AutocompleteCategoriesStyles;
    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    queryTitleStyling = (category: string, query: string | undefined) => {
        const queryArray = query ? category.toLowerCase().match(query.toLowerCase()) : null;
        const arr1: string[] = [];
        if (query && queryArray) {
            const queryIndex = queryArray.index ? queryArray.index : 0;
            arr1.push(category.substr(0, queryIndex));
            arr1.push("<span style='color:#006BC0; font-weight:700'>");
            arr1.push(category.substr(queryIndex, query.length));
            arr1.push("</span>");
            arr1.push(category.substr(queryIndex + query.length));
            return arr1.join("");
        }
        return category;
    };
    render() {
        const { categories } = this.props;
        if (!categories || categories.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteCategories_Header"
                    className="autocomplete-categories-header"
                >
                    {translate("Categories")}
                </Typography>
                {categories.map(category => (
                    <Fragment key={`${category.id}`}>
                        {category.subtitle && (
                            <Typography
                                {...(this.props.focusedItem === category
                                    ? styles.focusedCategoryItemText
                                    : styles.categoryItemText)}
                                onClick={() => {
                                    this.props.goToUrl(category.url);
                                }}
                            >
                                <Link {...styles.link}>
                                    <span
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: this.queryTitleStyling(category.title, this.props.focusedQuery),
                                        }}
                                    ></span>
                                </Link>
                                <>
                                    {` ${translate("in")}`} &nbsp;
                                    <span
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: this.queryTitleStyling(category.subtitle, this.props.focusedQuery),
                                        }}
                                    ></span>
                                </>
                            </Typography>
                        )}
                        {!category.subtitle && (
                            <Link
                                {...(this.props.focusedItem === category ? styles.focusedLink : styles.link)}
                                onClick={() => {
                                    this.props.goToUrl(category.url);
                                }}
                            >
                                <span
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: this.queryTitleStyling(category.title, this.props.focusedQuery),
                                    }}
                                ></span>
                            </Link>
                        )}
                    </Fragment>
                ))}
            </>
        );
    }
}

export default AutocompleteCategories;
